import { useMemo } from 'react';
import { components } from '@lifeserver/models/game-elements';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const DEFAULT_BASE_PATH = '/api';
const PREFIX = '/game-elements/v2';
const GET_GAME_ELEMENTS_PATH = `${PREFIX}/game-elements`;

const MAIN_BRANCH = 'main';

type GetGameElementDto = components['schemas']['GetGameElementDTO'];
export type GameElementDto = components['schemas']['GameElementDTO'];

class GameElementsApi {
    getAllGameElements = async ({
        basePath = DEFAULT_BASE_PATH,
        includeBranches = false,
    } = {}): Promise<GetGameElementDto> => {
        const query = includeBranches ? '?includeBranches=true' : '';
        const { data } = await axios.get<GetGameElementDto>(
            `${basePath}${GET_GAME_ELEMENTS_PATH}${query}`,
        );

        return data;
    };

    getGameElement = async (
        id: string,
        { basePath = DEFAULT_BASE_PATH } = {},
    ): Promise<GameElementDto> => {
        const { data } = await axios.get<GameElementDto>(
            `${basePath}${GET_GAME_ELEMENTS_PATH}/${id}`,
        );

        return data;
    };
}

const useGameElementsApi = () => {
    return useMemo(() => new GameElementsApi(), []);
};

export const useGameElements = ({ enabled = true } = {}) => {
    const gameElementsApi = useGameElementsApi();

    return useQuery({
        queryKey: [GET_GAME_ELEMENTS_PATH],
        queryFn: () => gameElementsApi.getAllGameElements(),
        enabled,
    });
};

// Fetches every game element across every source branch. The dropdown shows
// them alongside main; consumers deduplicate on id and use `isOnMain` to warn
// when a referenced element only exists on a feature branch.
export const useGameElementsIncludingBranches = ({ enabled = true } = {}) => {
    const gameElementsApi = useGameElementsApi();

    return useQuery({
        queryKey: [GET_GAME_ELEMENTS_PATH, { includeBranches: true }],
        queryFn: () =>
            gameElementsApi.getAllGameElements({ includeBranches: true }),
        enabled,
    });
};

export const isOnMain = (element: GameElementDto): boolean =>
    element.branch === MAIN_BRANCH;

// Branches re-export prefabs that already exist on main; keep one row per id, main preferred.
export const preferMainById = (
    elements: GameElementDto[],
): Map<string, GameElementDto> => {
    const byId = new Map<string, GameElementDto>();
    for (const element of elements) {
        const existing = byId.get(element.id);
        if (!existing || (!isOnMain(existing) && isOnMain(element))) {
            byId.set(element.id, element);
        }
    }
    return byId;
};
